import React from 'react';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ReportProblemIcon from '@material-ui/icons/ReportProblem';

import { unoTheme } from '../../assets/styles/unoTheme';
import BulkUpdateFeedback, { formatCnpj } from '../utils/BulkUpdateFeedback';
import MyTable from '../utils/MyTable';
import UnoButton from '../utils/UnoButton';
import { getMonthName } from '../utils/utils';
import { countFundsPendenciesIssues } from '../utils/fundsPendenciesUtils';

const useStyles = makeStyles((theme) => ({

    dialogTitle: {
        '& h2': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
        }
    },

    subtitle: {
        color: unoTheme.defaultTextColor,
        fontSize: '14px',
    },

    contentModal: {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'white',
    },

    actionsFlex: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
    },

    actionsRight: {
        display: 'flex',
        alignItems: 'center',
    },

    summaryChip: {
        display: 'inline-block',
        padding: '4px 14px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
    },

    countChip: {
        display: 'inline-block',
        padding: '2px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
        marginLeft: theme.spacing(1),
    },

    section: {
        borderLeft: 'solid 4px',
        borderRadius: '4px',
        background: unoTheme.secondaryTuplaTable,
        padding: '12px 16px',
        marginBottom: theme.spacing(2),
    },

    sectionHeader: {
        display: 'flex',
        alignItems: 'center',

        '& .MuiSvgIcon-root': {
            marginRight: theme.spacing(1),
        }
    },

    sectionTitle: {
        fontSize: '15px',
        fontWeight: 600,
        color: 'black',
    },

    sectionDescription: {
        fontSize: '12px',
        color: unoTheme.defaultTextColor,
        marginTop: '2px',
        marginLeft: '32px',
    },

    sectionOk: {
        fontSize: '12px',
        color: unoTheme.successColor,
        marginTop: '4px',
        marginLeft: '32px',
    },

    sectionTable: {
        marginTop: theme.spacing(1),
        background: 'white',
        borderRadius: '10px',
    },

    complementCell: {
        color: unoTheme.defaultTextColor,
    },

    safetyNote: {
        fontSize: '12px',
        color: unoTheme.defaultTextColor,
        borderTop: 'solid 1px #E6E8ED',
        paddingTop: theme.spacing(1),
        marginTop: theme.spacing(1),
    },
}));

//Fundos sem cotas e sem dados cadastrais travam o lançamento (launchWallet exige noQuotasNow e
//assetsNoInfos vazios) e por isso são erro. Cota replicada é o único caso de aviso: o lançamento
//segue, só se registra que aquele dia repetiu a cota anterior.
function getSectionColor(hasIssues, severity) {

    if (!hasIssues) return unoTheme.successColor;

    //'warning' é usado pelo bloco de cotas replicadas, que é apenas informativo: não impede
    //o lançamento e não tem ação em massa associada.
    return severity === 'warning' ? unoTheme.warningColorStrong : unoTheme.errorColor;
}

/**
 * Código que será usado na Comdinheiro além do CNPJ puro (ticker, sufixo ou subclasse).
 * Mesma precedência de getCodeToComdinheiroQuotas.
 */
function getComdinheiroComplement(fund) {

    if (fund.ticker) return 'ticker: ' + fund.ticker;
    if (fund.suffix) return '_' + fund.suffix;
    if (fund.subclass) return fund.subclass;
    return '-';
}

/**
 * Bloco de uma verificação. O cabeçalho é sempre exibido: em verde quando nada foi encontrado,
 * em vermelho quando há pendências e em âmbar quando `severity` é 'warning' (apenas aviso).
 */
function AnalysisSection({ title, description, count, severity, children }) {

    const classes = useStyles();

    const hasIssues = count > 0;
    const color = getSectionColor(hasIssues, severity);

    return (
        <div className={classes.section} style={{ borderLeftColor: color }}>

            <div className={classes.sectionHeader}>
                {
                    !hasIssues ?
                        <CheckCircleIcon style={{ color: color }} />
                        :
                        <ReportProblemIcon style={{ color: color }} />
                }

                <span className={classes.sectionTitle}>{title}</span>

                {
                    hasIssues ?
                        <span
                            className={classes.countChip}
                            style={{ background: color, color: 'white' }}>
                            {count}
                        </span>
                        : null
                }
            </div>

            {
                description ?
                    <div className={classes.sectionDescription}>{description}</div>
                    : null
            }

            {
                hasIssues ?
                    <div className={classes.sectionTable}>{children}</div>
                    :
                    <div className={classes.sectionOk}>{'Nenhuma pendência encontrada'}</div>
            }

        </div>
    )
}

function PendenciesTable({ funds, keyPrefix, showComplement }) {

    const classes = useStyles();

    return (
        <MyTable sizeheaders="verySmall" size="small" heightAuto noscroll>
            <colgroup>
                <col width={showComplement ? '58%' : '72%'} />
                <col width={showComplement ? '24%' : '28%'} />
                {showComplement ? <col width="18%" /> : null}
            </colgroup>
            <TableHead>
                <TableRow>
                    <TableCell align="left">{'Fundo'}</TableCell>
                    <TableCell align={showComplement ? 'center' : 'right'}>{'CNPJ'}</TableCell>
                    {showComplement ? <TableCell align="right">{'Sufixo/Subclasse'}</TableCell> : null}
                </TableRow>
            </TableHead>
            <TableBody>
                {funds.map((fund, index) => (
                    <TableRow key={keyPrefix + index}>
                        <TableCell align="left">{fund.fund_name}</TableCell>
                        <TableCell align={showComplement ? 'center' : 'right'}>
                            {formatCnpj(fund.cnpj)}
                        </TableCell>
                        {
                            showComplement ?
                                <TableCell align="right" className={classes.complementCell}>
                                    {getComdinheiroComplement(fund)}
                                </TableCell>
                                : null
                        }
                    </TableRow>
                ))}
            </TableBody>
        </MyTable>
    )
}

/**
 * Fundos cuja série teve dias sem cotação e foi completada com a última cota conhecida.
 * Diferente das outras listas, não há ação em massa: a cota faltante não existe na Comdinheiro
 * (papel sem negociação no dia) e só pode ser corrigida à mão no cadastro do fundo.
 */
function ReplicatedQuotasTable({ funds }) {

    return (
        <MyTable sizeheaders="verySmall" size="small" heightAuto noscroll>
            <colgroup>
                <col width="46%" />
                <col width="24%" />
                <col width="30%" />
            </colgroup>
            <TableHead>
                <TableRow>
                    <TableCell align="left">{'Fundo'}</TableCell>
                    <TableCell align="center">{'CNPJ'}</TableCell>
                    <TableCell align="right">{'Dias replicados'}</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {funds.map((fund, index) => (
                    <TableRow key={'fund-replicated-quotas-' + index}>
                        <TableCell align="left">{fund.fund_name}</TableCell>
                        <TableCell align="center">{formatCnpj(fund.cnpj)}</TableCell>
                        <TableCell align="right">
                            {(fund.replicatedDays || []).join(', ')}
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </MyTable>
    )
}

/**
 * Fundo usado como calendário para replicar as cotas dos fundos especiais e os dias em que ele
 * diverge dos dias úteis do mês (feriados ANBIMA).
 */
function CalendarDivergenceTable({ divergence }) {

    return (
        <MyTable sizeheaders="verySmall" size="small" heightAuto noscroll>
            <colgroup>
                <col width="34%" />
                <col width="18%" />
                <col width="24%" />
                <col width="24%" />
            </colgroup>
            <TableHead>
                <TableRow>
                    <TableCell align="left">{'Ativo modelo'}</TableCell>
                    <TableCell align="center">{'CNPJ'}</TableCell>
                    <TableCell align="right">{'Dias úteis sem cota no modelo'}</TableCell>
                    <TableCell align="right">{'Dias do modelo fora dos dias úteis'}</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                <TableRow>
                    <TableCell align="left">{divergence.fund_name}</TableCell>
                    <TableCell align="center">{formatCnpj(divergence.cnpj)}</TableCell>
                    <TableCell align="right">
                        {divergence.missingDays.length ? divergence.missingDays.join(', ') : '-'}
                    </TableCell>
                    <TableCell align="right">
                        {divergence.extraDays.length ? divergence.extraDays.join(', ') : '-'}
                    </TableCell>
                </TableRow>
            </TableBody>
        </MyTable>
    )
}

export default function FundsPendenciesModal({
    analysis,
    period,
    open,
    closeModal,
    progress,
    result,
    onUpdateQuotas,
    onUpdateInfos }) {

    const classes = useStyles();

    const { fundsNoQuotas = [], fundsNoInfos = [], fundsReplicatedQuotas = [], calendarDivergence = null } = analysis || {};

    const totalIssues = countFundsPendenciesIssues(analysis);

    const running = !!(progress && progress.running);

    const periodLabel = period && period.month && period.year ?
        getMonthName(period.month) + '/' + period.year : '';

    return (
        <Dialog
            fullWidth={true}
            maxWidth={'md'}
            open={open}
            disableBackdropClick={running}
            disableEscapeKeyDown={running}
            onClose={() => { if (!running) closeModal() }}>

            <DialogTitle className={classes.dialogTitle}>
                <div>
                    {'Pendências para o lançamento'}
                    <Typography className={classes.subtitle}>
                        {periodLabel}
                    </Typography>
                </div>

                <span
                    className={classes.summaryChip}
                    style={{
                        //Só cotas replicadas não é pendência de bloqueio: o chip fica em aviso
                        background: getSectionColor(totalIssues > 0,
                            !fundsNoQuotas.length && !fundsNoInfos.length ? 'warning' : undefined),
                        color: 'white',
                    }}>
                    {totalIssues > 0 ?
                        totalIssues + (totalIssues > 1 ? ' pendências' : ' pendência')
                        : 'Nenhuma pendência'}
                </span>
            </DialogTitle>

            <DialogContent className={classes.contentModal}>

                <BulkUpdateFeedback progress={progress} result={result} />

                <AnalysisSection
                    title={'Fundos sem cotas'}
                    description={'Fundos do cliente sem cotas diárias salvas localmente para o mês. Fundos especiais não entram nesta lista, pois não trazem cotas da Comdinheiro.'}
                    count={fundsNoQuotas.length}>

                    <PendenciesTable
                        funds={fundsNoQuotas}
                        keyPrefix={'fund-no-quotas-'}
                        showComplement />
                </AnalysisSection>

                <AnalysisSection
                    title={'Fundos sem dados cadastrais'}
                    description={'Fundos do cliente sem dados cadastrais salvos localmente para o mês. Fundos marcados como S/INFOS não entram nesta lista.'}
                    count={fundsNoInfos.length}>

                    <PendenciesTable
                        funds={fundsNoInfos}
                        keyPrefix={'fund-no-infos-'} />
                </AnalysisSection>

                <AnalysisSection
                    title={'Fundos com cota replicada'}
                    description={'Dias em que o fundo não teve cotação e a última cota conhecida foi repetida para não zerar o PL do ativo. Não impede o lançamento; corrija a cota no cadastro do fundo se o valor for conhecido.'}
                    count={fundsReplicatedQuotas.length}
                    severity={'warning'}>

                    <ReplicatedQuotasTable funds={fundsReplicatedQuotas} />
                </AnalysisSection>

                <AnalysisSection
                    title={'Calendário do ativo modelo'}
                    description={'As cotas dos fundos especiais são replicadas sobre os dias do fundo com a série de cotas mais completa, completados com os dias úteis do mês. Quando os dois não batem, confira se o fundo modelo está com cotas faltando ou se algum feriado está fora do calendário. Não impede o lançamento.'}
                    count={calendarDivergence ? 1 : 0}
                    severity={'warning'}>

                    {calendarDivergence ? <CalendarDivergenceTable divergence={calendarDivergence} /> : null}
                </AnalysisSection>

                <div className={classes.safetyNote}>
                    {'A atualização em massa nunca sobrescreve cotas ou dados cadastrais já gravados: '
                        + 'fundos que já possuem a informação no mês são recusados pelo servidor e '
                        + 'aparecem como falha. Cotas digitadas à mão (fundo especial ou cota '
                        + 'específica de cliente) ficam fora da lista.'}
                </div>

            </DialogContent>

            <DialogActions>
                <div className={classes.actionsFlex}>

                    <div className={classes.actionsRight}>
                        <UnoButton
                            type='primary'
                            size='small'
                            disabled={running || !fundsNoQuotas.length}
                            onClick={onUpdateQuotas}>
                            {'Atualizar cotas' + (fundsNoQuotas.length ? ' (' + fundsNoQuotas.length + ')' : '')}
                        </UnoButton>

                        <UnoButton
                            type='primary'
                            size='small'
                            margin='mLeft'
                            disabled={running || !fundsNoInfos.length}
                            onClick={onUpdateInfos}>
                            {'Atualizar dados cadastrais' + (fundsNoInfos.length ? ' (' + fundsNoInfos.length + ')' : '')}
                        </UnoButton>
                    </div>

                    <UnoButton
                        disabled={running}
                        onClick={closeModal}
                        color="secondary">
                        {'Fechar'}
                    </UnoButton>

                </div>
            </DialogActions>

        </Dialog>
    );
}
